import { EditOutlined, PlusOutlined, StopOutlined, ZoomInOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Badge, Button } from 'antd';
import theme from 'shared/constants/theme.json';
import Page from 'components/Page';
import StatusTag from 'components/StatusTag';
import Table from 'components/Table';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useCallback, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { reachedLicenseLimit } from 'shared/utils/license';
import EditSessionRecording from './EditSessionRecording';

const extraFields = `
  website {
    id
    matomoId
    matomoSessionRecordings {
      id
      name
      status
      sampleRate
      lastSample
      rules {
        attribute
        type
        value
        value2
        inverted
      }
      minSessionTime
      requiresActivity
      captureKeystrokes
      samples
      matomoHsrId
      createdAt
    }
  }
`;

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    type: 'date',
    defaultSortOrder: 'descend',
  },
  {
    title: 'Sample rate',
    dataIndex: 'sampleRate',
    render: (value) => `${value}%`,
    type: 'number',
  },
  {
    title: 'Samples',
    dataIndex: 'samples',
    type: 'number',
  },
  {
    title: 'Last sample',
    dataIndex: ['lastSample', 0],
    type: 'relativeDate',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: status => <StatusTag status={status} />,
    align: 'center',
  },
];

const viewPath = '/data-sources/:dataSourceId/hsr/session-recordings/:matomoHsrId';

const STOP_SESSION_RECORDING = gql`
  mutation($websiteId: ID!, $matomoHsrId: Int!) {
    stopMatomoSessionRecording(websiteId: $websiteId, matomoHsrId: $matomoHsrId)
  }
`;

const SessionRecordingList = () => {
  const { dataSource, dataSourceId, loading, getDataSourceLicenseSetting, refetch } = useDataSource({ extraFields });
  const [editSessionRecording, setEditSessionRecording] = useState(false);
  const [stopSessionRecording, { loading: stopping }] = useMutation(STOP_SESSION_RECORDING);

  const handleStop = async (item) => {
    await stopSessionRecording({
      variables: {
        websiteId: dataSource.website.id,
        matomoHsrId: item.matomoHsrId,
      },
    });
    refetch();
  };

  const handleDone = () => {
    setEditSessionRecording(false);
    refetch();
  };

  const sessionRecordings = dataSource?.website?.matomoSessionRecordings || [];
  const limit = getDataSourceLicenseSetting('sessionRecordingLimit');
  const canCreate = !reachedLicenseLimit(sessionRecordings?.length, limit);

  const actionsMenuItems = (record) => [
    {
      danger: true,
      key: 'stop',
      label: 'Stop',
      icon: <StopOutlined />,
      onClick: () => handleStop(record),
      visible: record.status !== 'ENDED',
    },
  ];

  const actions = useCallback((record) => [
    <Link key="results" to={generatePath(viewPath, { dataSourceId, matomoHsrId: record.matomoHsrId })}>
      <Button icon={<ZoomInOutlined />}>View</Button>
    </Link>,
    <Button key="edit" onClick={() => setEditSessionRecording(record)} icon={<EditOutlined />}>Edit</Button>,
  ], []);

  return (
    <Page>
      <Page.Header
        title="Recordings"
        extra={[
          <Badge key="usage" style={{ backgroundColor: theme[canCreate ? 'success-color' : 'error-color'] }} count={`${sessionRecordings.length || 0} / ${limit >= 0 ? limit : '∞'}`} showZero />,
          <Button key="create-session-recording" style={{ margin: '1em 0' }} block type="primary" onClick={() => setEditSessionRecording(true)} icon={<PlusOutlined />} disabled={!canCreate} className="create-heatmap">Create recording</Button>,
        ]}
      />
      <Page.Content>
        <Table loading={loading || stopping} dataSource={sessionRecordings} columns={columns} actions={actions} actionsMenuItems={actionsMenuItems} />
        <EditSessionRecording sessionRecording={editSessionRecording} open={!!editSessionRecording} onCancel={() => setEditSessionRecording(false)} onDone={handleDone} websiteId={dataSource?.website.id} />
      </Page.Content>
    </Page>
  );
};

export default SessionRecordingList;
